import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, Injector } from '@angular/core';
import { hasRecordLocation } from '../../../../../../../../../../shared/helpers/design-context';
import { confirmLinkRemoval, linkHeldStrokes } from '../../../../../../../../../../shared/helpers/link-removal';
import { DrawingsAvailabilityService } from '../../../../../../../../../../shared/services/drawings-availability.service';
import { DrawingsAnalytics } from '../../../../../../../../../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/drawings-analytics';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { DailyReportService, FormPickerContext } from '../../../../daily-report.service';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { allBidItems, currentLocation, dailyReport, projectDetails, siteDetails } from '../../../../../../../../core/projects.selectors';
import { debounceTime, skip, take, takeUntil } from 'rxjs/operators';
import { LocationPickerLauncherService } from '../../../../../../../../../../shared/services/location-picker-launcher.service';
import { QuestionsComponent } from '../questions/questions.component';
import { SignatureComponent } from '../signature/signature.component';
import { StationRangeComponent } from '../station-range/station-range.component';
import { ActivatedRoute } from '@angular/router';
import { ExpressionEvaluatorService } from '../../../../../../../../../../shared/services/expression-evaluator.service';
import { dailyReportLocationConfig, exprEvalConfig } from '../../../../../../../../../../shared/core/shared.selectors';
import { FetchAllBidItemsRequest } from '../../../../../../../../core/projects.actions';
import { featureSourceMatchesLink, resolveFeatureSourcePrefill } from '../../../../../../../../../../shared/helpers/feature-source-prefill';
import type { FeatureSource } from '../../../../../../../../../../shared/models/control-form';
import type { MapFeatureLink } from '../../../../../../../../../../shared/models/map-feature-link';
import {
  featureLauncherOf, formTypeChoices, LauncherFormLike, matchLauncherRoute, ResolvedLauncherRoute, resolveLauncherRoutes,
} from '../../../../../../../../../../shared/helpers/feature-launcher';
import type { PickResult } from '../../../../../../../../../../shared/helpers/design-context-pick';
import { AUTOFILLED_BADGE, autofillSummary, PrefillCaptionTracker } from '../../../../../../../../../../shared/helpers/prefill-captions';
import { FeatureLinkEntry, findLinkedRecord, shortReportDate } from '../../../../../../../../../../shared/helpers/feature-link-status';

/**
 * Read-only presentation model for a captured form signature. Derived from the
 * raw signature object returned by the API so the template stays declarative
 * and the derivation stays unit-testable.
 */
export interface SignatureView {
  role: string | null;
  // Normalized capture method: 'drawn' renders the image (and, when present, the
  // printed name in `text` beneath it for legibility), 'typed_font' renders the
  // text in the chosen font, everything else renders plain text.
  method: 'drawn' | 'typed_font' | 'typed';
  // For drawn signatures this is the PRINTED NAME captured alongside the image
  // (may be empty); for typed signatures it's the typed signature text.
  text: string | null;
  // Resolved drawn-signature image URL (signature_image_url, else first picture).
  imageUrl: string | null;
  // CSS font-family value for typed_font signatures, else null.
  fontFamily: string | null;
  // Formatted signed-at timestamp (null when missing/unparseable).
  signedAt: string | null;
  // Index of this signer in the source signatures[] array. Because the view
  // list is filtered (only signers with content), the display index may differ
  // from the source index — edit/remove must target this one. Defaults to -1
  // when derived without a source position (e.g. unit tests).
  sourceIndex: number;
}

/**
 * Pure derivation of a SignatureView from a raw API signature object. Kept as a
 * free function (no `this`) so it is trivially unit-testable. Image URL
 * resolution prefers `signature_image_url`, then `pictures[0].url`.
 */
export function buildSignatureView(
  signature: any,
  datePipe: DatePipe = new DatePipe('en-US'),
  sourceIndex = -1,
): SignatureView {
  const rawMethod = signature?.signature_method;
  const text = signature?.signature_text ?? null;
  // The drawn-signature image is an attachment exposed by the API as
  // `pictures[0]` with `original_url` / `preview_url` / `thumb_url` (there is NO
  // plain `url`/`uri` field, and `signature_image_url` is only present on some
  // payloads). Resolve across all of these so saved drawn signatures render.
  const picture = signature?.pictures?.[0];
  const imageUrl =
    signature?.signature_image_url ||
    picture?.url ||
    picture?.uri ||
    picture?.original_url ||
    picture?.preview_url ||
    picture?.thumb_url ||
    null;

  let method: SignatureView['method'];
  if (rawMethod === 'drawn') {
    method = 'drawn';
  } else if (rawMethod === 'typed_font') {
    method = 'typed_font';
  } else {
    method = 'typed';
  }

  const fontFamily = method === 'typed_font' && signature?.signature_font
    ? `'${signature.signature_font}', cursive`
    : null;

  let signedAt: string | null = null;
  if (signature?.signed_at) {
    try {
      signedAt = datePipe.transform(signature.signed_at, 'hh:mm a on MMMM dd, yyyy') ?? null;
    } catch {
      signedAt = null;
    }
  }

  return {
    role: signature?.role ?? null,
    method,
    text,
    imageUrl,
    fontFamily,
    signedAt,
    sourceIndex,
  };
}

/**
 * Pure merge of a setSignature emission into an existing signatures[] slot.
 * Maps the dialog's `value` to `signature_text` and copies the capture metadata
 * (method/font/signed_at/pictures). When a field is absent on the emission the
 * prior value is preserved so re-saving a typed signature doesn't wipe an
 * existing drawn image, and vice versa. Free function (no `this`) for testing.
 */
/**
 * Pure derivation of the signature role options from a form's
 * ui_config.signature_roles (array of label strings). Falls back to the legacy
 * QA/QC pair when the template doesn't configure roles. Roles MAY repeat across
 * signers, so the returned option list is the raw role set, NOT de-duplicated by
 * already-used roles. Each option uses the role label as both label and value.
 * Free function (no `this`) for unit testing.
 */
export function buildSignatureRoleOptions(uiConfig: any): { label: string; value: string }[] {
  const configured = uiConfig?.signature_roles;
  const roles = Array.isArray(configured) && configured.length ? configured : ['QA', 'QC'];
  return roles
    .filter((r: any) => typeof r === 'string' && r.trim())
    .map((r: string) => ({ label: r, value: r }));
}

export function mergeSignatureEmission(existing: any, emission: any): any {
  const prev = existing || {};

  // KEEP emission (edit dialog confirmed without a redraw): update the role and
  // the printed name (signature_text) while preserving the existing image
  // capture (method/font/signed_at/pictures). This is what stops a role/name-only
  // edit — or closing/confirming without re-drawing — from wiping the existing
  // signature image. The printed name is taken from the emission when present so
  // an edited name persists; absent => keep the prior text.
  if (emission?.keep) {
    return {
      ...prev,
      role: emission?.role ?? prev.role ?? null,
      signature_text: emission?.value !== undefined ? emission.value : (prev.signature_text ?? null),
    };
  }

  return {
    ...prev,
    role: emission?.role ?? prev.role ?? null,
    signature_text: emission?.value ?? null,
    signature_method: emission?.signature_method ?? prev.signature_method ?? 'typed',
    signature_font: emission?.signature_font ?? null,
    signed_at: emission?.signed_at ?? prev.signed_at ?? null,
    pictures: Array.isArray(emission?.pictures) && emission.pictures.length
      ? emission.pictures
      : (prev.pictures ?? []),
  };
}

/**
 * Answers offered for a form question when the template doesn't configure
 * `ui_config.response_options` — same defaults the field app falls back to
 * (mobile FORM_QUESTION_DEFAULT_OPTIONS).
 */
export const QUESTION_DEFAULT_OPTIONS = ['Pass', 'Fail', 'N/A'];

/** A picker's starting point (store locations carry numbers or numeric strings). */
interface PickerSeed { latitude?: number | string | null; longitude?: number | string | null }

@Component({
    selector: 'app-control-forms-details',
    templateUrl: './control-forms-details.component.html',
    styleUrls: ['./control-forms-details.component.scss'],
    standalone: false
})
export class ControlFormsDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  /** W49: "Mark on drawing" in the location menu, for projects with published sheets */
  hasPublishedSheets = false;
  private sheetsCheckedFor: string | null = null;
  private readonly drawingsAvailability = inject(DrawingsAvailabilityService);
  private readonly drawingsTrack = inject(DrawingsAnalytics);
  private readonly injector = inject(Injector);
  @Input() data: any;
  // Read-only display mode (e.g. map drilldown panel). When options.disabled is
  // true the form is rendered disabled and the editing-only UI (form-type
  // dropdown, add/delete entry, save/delete, location/signature dialogs) is
  // hidden; mutation subscriptions are skipped. Default (absent) = full editor.
  // `preview: true` is set by Settings > Forms, which renders a BLANK instance of
  // an already-chosen template: the form-type dropdown is pointless there (and
  // the template may belong to another project, so it is not even in the list),
  // but the form stays interactive so sections/lines can be explored. Nothing is
  // saved — that only happens on DailyReportService.triggerSave.
  @Input() options: { disabled?: boolean, preview?: boolean } = {};

  get readOnly(): boolean {
    return !!this.options?.disabled;
  }

  get isPreview(): boolean {
    return !!this.options?.preview;
  }

  // In preview the questions screen is drawn INSIDE the phone frame (the field
  // app pushes a screen; a web dialog would break out of it), so the questions
  // are handed to the host instead of opening the dialog.
  @Output() previewQuestions = new EventEmitter<{ label: string, questions: any[], options: string[] }>();

  // Same for the signature sheet — the field app pushes its own screen.
  @Output() previewSignature = new EventEmitter<void>();

  /**
   * Dialogs open as full-page overlays, which in the Settings > Forms preview
   * escape the phone frame entirely and invite answering a form that is never
   * saved. Preview keeps the summary cards; tapping them does nothing.
   */
  private get dialogsSuppressed(): boolean {
    return this.readOnly || this.isPreview;
  }

  // Memoized normalized fallback template (keyed by control form id) so we don't
  // re-clone on every resolveFormTemplate() call.
  private normalizedFallbackTemplate: any = null;

  /**
   * Resolve the form TEMPLATE (control_form definition) for a given id. Prefers
   * the store-backed fromTypeOptions (content-outlet editor), which the project
   * effect has already enriched. Falls back to the template carried on
   * this.data.control_form (e.g. the map drilldown panel) — but that raw template
   * comes straight from the daily-report API and is MISSING the derived
   * `componentType` (and normalized pick_list_values) the field renderer relies
   * on ([ngSwitch]="hf?.componentType"). The map route's store projectDetails has
   * no control_forms, so fromTypeOptions is empty there — we must enrich the
   * fallback ourselves or header/line/section fields render blank.
   */
  private resolveFormTemplate(controlFormId: any): any {
    const storeTemplate = this.fromTypeOptions?.find(o => o?.id === controlFormId);
    if (storeTemplate) {
      return storeTemplate;
    }

    if (this.data?.control_form?.id !== controlFormId) {
      return null;
    }

    if (!this.normalizedFallbackTemplate) {
      this.normalizedFallbackTemplate = this.enrichControlFormTemplate(this.data.control_form);
    }
    return this.normalizedFallbackTemplate;
  }

  /**
   * Mirror of the enrichment the projects effect applies when loading
   * control_forms into the store (processFields): derive `componentType` for
   * header/footer/line fields and normalize inline pick_list_values to
   * {label, value}. Returns a deep-cloned template so we never mutate the shared
   * cached this.data.control_form.
   */
  private enrichControlFormTemplate(template: any): any {
    if (!template) {
      return template;
    }

    const clone = JSON.parse(JSON.stringify(template));

    const processFields = (fields: any[]) => (fields || []).map(field => {
      const isDropdown = !!field?.pick_list
        || field?.ui_config?.pick_list_values?.length
        || field?.multi_select;

      if (Array.isArray(field?.ui_config?.pick_list_values)) {
        field.ui_config.pick_list_values = field.ui_config.pick_list_values.map((plv: any) =>
          (plv && typeof plv === 'object') ? plv : { label: plv, value: plv },
        );
      }

      return { ...field, componentType: isDropdown ? 'dropdown' : field?.type };
    });

    if (clone?.ui_config?.sections?.length > 0) {
      clone.ui_config.sections = clone.ui_config.sections.map((section: any) => ({
        ...section,
        label: section?.label ?? section?.description,
        value: section?.value ?? section?.name,
      }));
    }
    if (clone?.ui_config?.header?.fields?.length > 0) {
      clone.ui_config.header.fields = processFields(clone.ui_config.header.fields);
    }
    if (clone?.ui_config?.footer?.fields?.length > 0) {
      clone.ui_config.footer.fields = processFields(clone.ui_config.footer.fields);
    }
    if (clone?.ui_config?.lines?.fields?.length > 0) {
      clone.ui_config.lines.fields = processFields(clone.ui_config.lines.fields);
    }

    return clone;
  }

  errorMessages: any = {};
  errorMessageMap = {
    formType: {
      required: 'This field is required.',
    },
    frequency: {
      required: 'This field is required.',
    },
  };
  form = new FormGroup<any>({
    formType: new FormControl({ value: '', disabled: false }, [Validators.required]),
    latitude: new FormControl({ value: null, disabled: false }),
    longitude: new FormControl({ value: null, disabled: false }),
    location: new FormControl({ value: null, disabled: false }),
    comment: new FormControl({ value: null, disabled: false }),
    pictures: new FormControl({ value: JSON.stringify([]), disabled: false }),
    videos: new FormControl({ value: JSON.stringify([]), disabled: false }),
    frequency: new FormControl({ value: null, disabled: false }),
    stationRangeFromStation: new FormControl({ value: null, disabled: false }),
    stationRangeFromOffsetType: new FormControl({ value: null, disabled: false }),
    stationRangeFromOffsetDistance: new FormControl({ value: null, disabled: false }),
    stationRangeToStation: new FormControl({ value: null, disabled: false }),
    stationRangeToOffsetType: new FormControl({ value: null, disabled: false }),
    stationRangeToOffsetDistance: new FormControl({ value: null, disabled: false }),
    alignment: new FormControl({ value: null, disabled: false }),
    signatureOne: new FormControl({ value: null, disabled: false }),
    signatureTwo: new FormControl({ value: null, disabled: false }),
    map_feature_link: new FormControl(null),
  });

  fromTypeOptions: any[] = [];
  /** The form-type selector's options: fromTypeOptions without `launcher_only` forms when adding (see formTypeChoices). */
  formTypeOptions: LauncherFormLike[] = [];
  /** Why a launcher form could not start (no routed form, map unavailable). */
  launcherMessage = '';
  /** Header fields the linked map feature filled (Figma "Autofilled" badge + summary card); session only, an edit clears one. */
  readonly prefillCaptions = new PrefillCaptionTracker(() => this.cdRef.markForCheck());
  readonly autofilledBadge = AUTOFILLED_BADGE;
  frequencyOptions: any[] = [];
  fields = [];
  lines = [];
  displayCommentSection = false;
  mediaFiles = [];
  videoToBeDeletedFromMuxDb = [];
  displaySignature = false;
  displayStationRange = false;
  controlForms: any;
  dailyReport: any;
  generalQuestions = [];
  sectionQuestions = {};
  displayFrequency = false;
  displayFrequencyQuestions = false;
  selectedFrequencyType: any = [];
  signaturesPresent = false;
  responseOptions: any;
  formTypeDetails: any;
  headerFields = [];
  lineFields = [];
  picklistOptions = {};
  generalQuestionPositiveArray = [];
  generalQuestionNegativeArray = [];
  generalQuestionNaArray = [];
  displayGeneralQuestionsDetails = false;
  answeredGeneralQuestion = [];
  displayFrequencyQuestionsDetails = false;
  frequencyQuestionSummary: any = {};
  // Dynamic list of captured signers. A form may have an arbitrary number of
  // signers and roles may repeat (e.g. 1 Entry Supervisor + 5 Entrants). Seeded
  // empty; the capture dialog appends/edits entries. Backward-compatible: forms
  // saved with the legacy fixed QA/QC pair load straight into this array.
  signatures: any = [];
  // Read-only presentation models derived from `signatures` (see
  // buildSignatureView). Populated whenever saved signatures are present.
  signatureViews: SignatureView[] = [];
  stationRange: any = {};
  stationRangeDisplayValue: any = null;
  alignment: any;
  expandedSection: any;
  project: any;
  footerFields: any = [];
  lineKeys: any = [];
  selectedLineId: any;
  lineFormGroup = new FormGroup<any>({});
  selectedMediaFiles: any[];
  exprEvalConfigExpressions: any;
  dewcForm = false;
  sectionAccordions: any = [];
  selectedSectionId: any;
  sectionTypeFieldsMap: any = {};
  selectedSectionFormGroup = new FormGroup<any>({});
  sectionLineKeys: any = [];
  sectionLineFields: any = [];
  selectedSectionLineBlockFormGroup = new FormGroup<any>({});
  selectedSectionLineBlockId: any;
  selectedSectionLineKeys: any = [];
  bidItemsList: any[] = [];
  dailyReportLocationConfig: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private dailyReportService: DailyReportService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private cdRef: ChangeDetectorRef,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private evaluator: ExpressionEvaluatorService,
    private locationPickerLauncher: LocationPickerLauncherService,
  ) {
  }

  ngOnInit() {
    if (!this.readOnly) {
      // Pickers opened from the action bar ("+ Add location") honour this form's feature_source and link too.
      this.dailyReportService.formPickerContext = this.pickerContext;
    }
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp && qp['expandedSection']) {
          this.expandedSection = qp['expandedSection'];
        }
      });

    this.store.select(exprEvalConfig)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((eec: any) => {
        this.exprEvalConfigExpressions = eec.expressions;
      });

    this.store.select(dailyReportLocationConfig)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        this.dailyReportLocationConfig = data;
        if (this.formTypeDetails) {
          this.displayStationRange = this.shouldDisplayStationRange(this.formTypeDetails);
        }
      });

    this.store.select(allBidItems)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((bidItems) => {
        if (bidItems?.length) {
          this.bidItemsList = bidItems?.map(o => {
            return {
              ...o,
              label: `${o?.item} ${o?.description}`,
              value: o?.id,
            };
          });
        }
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.project = project;
        this.checkPublishedSheets(project?.id);
        // Don't refetch bid items in read-only mode (e.g. the map drilldown panel).
        // FetchAllBidItemsRequest resets allBidItems to [] while it loads, and the
        // host MapLayerContainerComponent binds that same store slice
        // ([availableBidItems]) — firing it during the panel's change-detection pass
        // throws NG0100 (ExpressionChangedAfterItHasBeenChecked). The read-only
        // viewer only reads whatever bid items are already in the store.
        if (!this.readOnly) {
          this.store.dispatch(FetchAllBidItemsRequest({
            payload: {
              projectId: project?.id,
              qp: {
                include: [
                  {
                    bid_items: ['fields'],
                  },
                ],
              },
            },
          }));
        }

        if (project?.control_forms?.length) {
          const { control_forms } = project;

          const dailyReportForms = control_forms.filter((form: any) => form.display_widget === 'forms');
          const controlPointForms = control_forms.filter((form: any) => form.display_widget === 'control_forms');

          if (this.readOnly) {
            // Read-only viewers (e.g. the map drilldown panel) have no
            // expandedSection route context. Use the full store-side control_forms
            // list so resolveFormTemplate() finds the store-TRANSFORMED template —
            // which carries derived `componentType` and normalized pick_list_values
            // that the raw bulk-API control_form on this.data lacks. Without this,
            // header/line/section fields render blank ([ngSwitch] on componentType
            // matches nothing). Covers both 'forms' and 'control_forms' widgets.
            this.fromTypeOptions = control_forms;
          } else {
            switch (this.expandedSection) {
              case 'forms':
                this.fromTypeOptions = dailyReportForms;
                break;
              case 'control-forms':
                this.fromTypeOptions = controlPointForms;
                break;
              default:
                this.fromTypeOptions = [];
            }
          }
        } else {
          this.fromTypeOptions = [];
        }
        this.refreshFormTypeOptions();
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.dailyReport = JSON.parse(JSON.stringify(report));
        const tempControlPointForms = this.dailyReport?.reportDetails?.controlForms?.controlPointForms || [];
        const tempDailyReportForms = this.dailyReport?.reportDetails?.controlForms?.dailyReportForms || [];
        this.controlForms = [...tempControlPointForms, ...tempDailyReportForms];
      });

    this.form?.controls?.['formType']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.lineKeys = [];
        this.sectionAccordions = [];
        this.selectedMediaFiles = [];
        this.mediaFiles = [];
        this.prefillCaptions.clear();
        this.refreshFormTypeOptions();
        // A launcher form (ui_config.feature_launcher) is never filled in itself: it opens the map to pick a feature.
        const launcherForm = this.fromTypeOptions?.find(o => o?.id === data);
        if (!this.data && !this.readOnly && featureLauncherOf(launcherForm)) {
          this.startFeatureLauncher(launcherForm);
          return;
        }
        let isControlFromPresent = false;
        let selectedForm: any;
        if (data) {
          selectedForm = this.controlForms?.find(o => o?.control_form?.id === data);
          isControlFromPresent = !!selectedForm;
        }

        const allowMultipleForms =
          selectedForm?.control_form?.ui_config?.allow_multiple_forms || false;

        if (!allowMultipleForms && isControlFromPresent) {
          this.dailyReportService.openDetailsPanel.next({
            type: 'forms',
            formData: selectedForm,
          });
          return;
        }

        const formTypeDetails = this.fromTypeOptions?.filter(o => o?.id === data)[0];
        this.formTypeDetails = formTypeDetails;
        this.fields = [];
        this.displayFrequencyQuestions = false;
        this.displayGeneralQuestionsDetails = false;
        this.fields = formTypeDetails?.fields || [];
        this.generalQuestions = [];
        this.responseOptions = formTypeDetails?.ui_config?.response_options || [];
        this.displaySignature = formTypeDetails?.ui_config?.display_signatures;
        this.displayStationRange = this.shouldDisplayStationRange(formTypeDetails);
        this.headerFields = [...(formTypeDetails?.ui_config?.header?.fields ?? [])]
          .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0));
        this.lineFields = [...(formTypeDetails?.ui_config?.lines?.fields ?? [])]
          .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0));
        this.dewcForm = formTypeDetails?.ui_config?.sections?.length && formTypeDetails?.display_format === 'header_lines';

        if (this.dewcForm) {
          this.sectionAccordions = formTypeDetails?.ui_config?.sections;
          this.sectionAccordions.forEach(o => {
            this.form?.addControl(
              'section_' + o?.name,
              new FormGroup({}),
            );
          });
          this.createSectionTypeFieldsMap(formTypeDetails?.ui_config?.lines?.fields, formTypeDetails?.ui_config?.sections);
        }

        this.createPicklistOptions();

        this.headerFields?.forEach((field: any) => {
          this.form?.addControl(
            field?.name,
            new FormControl({ value: null, disabled: !!field?.display_only },
              field?.is_optional === false ? [Validators.required] : [],
            ),
          );
        });

        this.headerFields.forEach(hf => {
          let disCond;
          let disCondField;

          if (hf?.ui_config?.display_condition) {
            disCond = hf?.ui_config?.display_condition;
            disCondField = hf;
          }

          if (disCond) {
            const temp = this.headerFields?.find(o => disCond?.includes(o?.name));

            if (!this.form.controls[temp?.name]?.value) {
              this.form.removeControl(disCondField?.name);
            } else {
              this.form?.addControl(
                disCondField?.name,
                new FormControl({ value: null, disabled: !!disCondField?.display_only },
                  disCondField?.is_optional === false ? [Validators.required] : [],
                ),
              );
            }

            const isProgrammaticUpdate = false;

            this.form.controls[temp?.name]?.valueChanges
              .pipe(takeUntil(this.onDestroy))
              .subscribe(d => {

                if (isProgrammaticUpdate) {
                  return;
                }

                if (d === true) {
                  this.form?.addControl(
                    disCondField?.name,
                    new FormControl({ value: null, disabled: !!disCondField?.display_only },
                      disCondField?.is_optional === false ? [Validators.required] : [],
                    ),
                  );
                } else {
                  this.form.removeControl(disCondField?.name);
                }

                if (disCond.includes(d)) {
                  this.form?.addControl(
                    disCondField?.name,
                    new FormControl({ value: null, disabled: !!disCondField?.display_only },
                      disCondField?.is_optional === false ? [Validators.required] : [],
                    ),
                  );
                } else {
                  this.form.removeControl(disCondField?.name);
                }

                if (d === '_others') {
                  this.form?.addControl(
                    temp?.name + '_others',
                    new FormControl({ value: null, disabled: false },
                      [Validators.required],
                    ),
                  );
                } else {
                  this.form.removeControl(temp?.name + '_others');
                }
              });
          }
        });

        this.fields.forEach((field) => {
          const { ui_config } = field;

          if (!ui_config || Object.keys(ui_config)?.length === 0) {
            this.generalQuestions.push(field);
          } else if (ui_config?.section_name) {
            const sectionKey = `${ui_config?.section_name}`;

            if (!this.sectionQuestions[sectionKey]) {
              this.sectionQuestions[sectionKey] = [];
            }

            this.sectionQuestions[sectionKey].push(field);
          }
        });

        Object.keys(this.sectionQuestions).forEach((key) => {
          const questions = this.sectionQuestions[key];
          this.createFrequencyQuestionsFrom(questions, this.lines, key);
          this.frequencyQuestionSummary[key] = {
            pass: [],
            fail: [],
            na: [],
            answered: [],
          };
        });

        if (formTypeDetails?.ui_config && formTypeDetails?.ui_config?.sections?.length > 0 && !this.dewcForm) {
          this.displayFrequency = true;
          this.frequencyOptions = formTypeDetails?.ui_config?.sections;
        }

        this.createGeneralQuestionsFrom(this.generalQuestions, this.lines);
      });

    this.form?.controls?.['frequency']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.displayFrequencySection(data);
      });

    if (!this.readOnly) {
      this.form.valueChanges
        .pipe(
          skip(1),
          takeUntil(this.onDestroy))
        .subscribe(() => {
          this.projectsService.isAnyFormDirty.next(true);
        });
    }

    if (this.data) {
      this.form.controls['formType'].disable({ emitEvent: false });
      // A saved record gets its formType from the dropdown; a preview has no
      // dropdown, and the line/section blocks are gated on that control having a
      // value — so seed it (without emitting, which would reset the rebuild).
      if (this.isPreview && this.data?.control_form?.id) {
        this.form.controls['formType'].setValue(this.data.control_form.id, { emitEvent: false });
      }
      this.lineKeys = [];
      this.mediaFiles = [...this.data?.pictures, ...this.data?.videos];

      if (this.data?.signatures?.length) {
        // Load the saved signers straight into the dynamic array (any count,
        // duplicate roles allowed). Mirror the first two into the legacy
        // signatureOne/Two controls for backward compatibility with the typed
        // save fallback on older forms.
        this.signatures = this.data?.signatures;
        this.refreshSignatureViews();
        this.form.controls?.['signatureOne'].setValue(this.data?.signatures[0]?.signature_text, { emitEvent: false });
        this.form.controls?.['signatureTwo'].setValue(this.data?.signatures[1]?.signature_text, { emitEvent: false });
      }

      if (this.data?.comment) {
        this.displayCommentSection = true;
      }

      if (this.isValidObject(this.data?.station_range?.to) || this.isValidObject(this.data?.station_range?.from)) {
        this.displayStationRange = true;
        this.setStationRange(this.data?.station_range);
      }

      this.displaySignature = this.data?.control_form?.ui_config?.display_signatures;
      this.signaturesPresent = this.data?.signatures.length > 0;

      const controlFormId = this.data?.control_form?.id;
      this.lines = this.data?.lines;
      const resolvedTemplate = this.resolveFormTemplate(controlFormId);
      this.fields = resolvedTemplate?.fields || [];
      this.responseOptions = resolvedTemplate?.ui_config?.response_options || [];

      this.fields.forEach((field) => {
        const { ui_config } = field;

        if (!ui_config || Object.keys(ui_config).length === 0) {
          this.generalQuestions.push(field);
        } else if (ui_config?.section_name) {
          const sectionKey = `${ui_config?.section_name}`;
          if (!this.sectionQuestions[sectionKey]) {
            this.sectionQuestions[sectionKey] = [];
          }

          this.sectionQuestions[sectionKey].push(field);
        }
      });

      Object.keys(this.sectionQuestions).forEach((key) => {
        const questions = this.sectionQuestions[key];
        this.createFrequencyQuestionsFrom(questions, this.lines, key);
        this.frequencyQuestionSummary[key] = {
          pass: [],
          fail: [],
          na: [],
          answered: [],
        };
      });

      if (this.data?.control_form?.id) {
        const formTypeId = this.data?.control_form?.id;
        const formTypeDetails = this.resolveFormTemplate(formTypeId);
        this.formTypeDetails = formTypeDetails;
        this.dewcForm = formTypeDetails?.ui_config?.sections?.length && formTypeDetails?.display_format === 'header_lines';

        if (this.dewcForm) {
          this.sectionAccordions = formTypeDetails?.ui_config?.sections;
          this.sectionAccordions.forEach(o => {
            this.form?.addControl(
              'section_' + o?.name,
              new FormGroup({}),
            );
          });
          this.createSectionTypeFieldsMap(formTypeDetails?.ui_config?.lines?.fields, formTypeDetails?.ui_config?.sections);
        }

        this.headerFields = [...(formTypeDetails?.ui_config?.header?.fields ?? [])]
          .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0));

        this.lineFields = [...(formTypeDetails?.ui_config?.lines?.fields ?? [])]
          .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0));

        if (this.dewcForm) {
          this.data?.lines?.forEach((line: any) => {
            line.field_values = line.field_values?.filter((o: any) => o?.value);
          });

          Object.entries(this.form.controls || {})
            .filter(([key]) => key.startsWith('section_'))
            .forEach(([key, control]: any) => {
              const tempKey = key.split('_')[1];
              const sectionLineFields = this.sectionTypeFieldsMap?.[tempKey] || [];

              // find lines that belong to this section based on matching field ids
              const sectionLines = this.data?.lines?.filter((line: any) => {
                return line.field_values.some((fv: any) =>
                  sectionLineFields.some((sf: any) => sf.id === fv.id),
                );
              }) || [];

              sectionLines?.forEach((line: any, lIndex: number) => {
                const lineKey = `line_${lIndex + 1}`;
                if (control.get(lineKey)) {
                  return;
                }

                const lineGroup = new FormGroup({});

                sectionLineFields.forEach((lf: any) => {
                  const matchedVal = line?.field_values.find((fv: any) => fv?.id === lf?.id);
                  const value = typeof matchedVal?.value === 'object'
                    ? matchedVal?.value?.value
                    : matchedVal?.value ?? null;

                  lineGroup.addControl(
                    lf.name,
                    new FormControl(
                      { value, disabled: !!lf?.display_only },
                      lf?.is_optional === false ? [Validators.required] : [],
                    ),
                  );
                });

                lineGroup.addControl('pictures', new FormControl(JSON.stringify(line?.pictures || [])));
                lineGroup.addControl('videos', new FormControl(JSON.stringify(line?.videos || [])));

                control.addControl(lineKey, lineGroup);
              });
              this.evaluateSectionLineFormulas();
            });
        } else {
          this.data?.lines.forEach((line: any, lIndex: number) => {

            const lineKey = `line_${lIndex + 1}`;
            const lineGroup = new FormGroup({});

            this.lineFields?.forEach((lf: any) => {
              const matchedVal = line.field_values.find((fv: any) => fv.id === lf.id);
              let value = typeof matchedVal?.value === 'object'
                ? matchedVal?.value?.value
                : matchedVal?.value ?? null;

              if (/_time|time/i.test(lf.name) && typeof value === 'string') {
                const amPmRegex = /^(0?[1-9]|1[0-2]):[0-5]\d\s?(AM|PM)$/i;

                if (/^\d{2}:\d{2}:\d{2}$/.test(value)) {
                  // HH:MM:SS → hh:mm AM/PM
                  const [hourStr, minuteStr] = value.split(':');
                  let hour = parseInt(hourStr, 10);
                  // tslint:disable-next-line:variable-name
                  const am_pm = hour >= 12 ? 'PM' : 'AM';
                  hour = hour % 12 || 12;
                  value = `${hour.toString().padStart(2, '0')}:${minuteStr} ${am_pm}`;
                } else if (/^\d{2}:\d{2}$/.test(value) && !amPmRegex.test(value)) {
                  // HH:MM → hh:mm AM/PM
                  const [hourStr, minuteStr] = value.split(':');
                  let hour = parseInt(hourStr, 10);
                  // tslint:disable-next-line:variable-name
                  const am_pm = hour >= 12 ? 'PM' : 'AM';
                  hour = hour % 12 || 12;
                  value = `${hour.toString().padStart(2, '0')}:${minuteStr} ${am_pm}`;
                }
                // If already AM/PM, do nothing
              }

              if (lf?.type === 'time') {
                let timePeriodVal = '';
                let amPmVal = '';

                if (typeof value === 'string') {
                  const [timePart, ampmPart] = value?.split(' ');
                  amPmVal = ampmPart || '';

                  const [hour, minute] = timePart?.split(':');
                  const paddedHour = hour?.padStart(2, '0');
                  const paddedMinute = minute?.padStart(2, '0');
                  timePeriodVal = `${paddedHour}:${paddedMinute}`;
                }

                const formattedTime = `${timePeriodVal}${amPmVal ? ' ' + amPmVal : ''}`;

                lineGroup.addControl(
                  lf?.name === 'time' ? 'time' : lf?.name?.includes('time') ? lf?.name : '',
                  new FormControl(
                    { value: formattedTime, disabled: !!lf?.display_only },
                    lf?.is_optional === false ? [Validators.required] : [],
                  ),
                );

                lineGroup.addControl(
                  lf?.name === 'time' ? 'timePeriod' : lf?.name?.includes('time') ? lf?.name + '_timePeriod' : '',
                  new FormControl({
                    value: timePeriodVal,
                    disabled: false,
                  }));

                lineGroup.addControl(
                  lf?.name === 'time' ? 'am_pm' : lf?.name?.includes('time') ? lf?.name + '_am_pm' : '',
                  new FormControl({
                    value: amPmVal,
                    disabled: false,
                  }));
              } else {
                lineGroup.addControl(
                  lf.name,
                  new FormControl(
                    { value, disabled: !!lf?.display_only },
                    lf?.is_optional === false ? [Validators.required] : [],
                  ),
                );
              }

              lineGroup.addControl('pictures', new FormControl({ value: JSON.stringify(line?.pictures), disabled: false }));
              lineGroup.addControl('videos', new FormControl({ value: JSON.stringify(line?.videos), disabled: false }));
            });

            this.form.addControl(lineKey, lineGroup);

            this.evaluateLineFormulas(lineGroup);

            this.lineKeys = this.getLineKeys() || [];
          });
        }

        this.createPicklistOptions();

        this.headerFields?.forEach((field: any) => {
          this.form?.addControl(
            field?.name,
            new FormControl({ value: null, disabled: !!field?.display_only },
              field?.is_optional === false ? [Validators.required] : [],
            ),
          );
        });

        this.headerFields?.forEach(hf => {
          let disCond;
          let disCondField;

          if (hf?.ui_config?.display_condition) {
            disCond = hf?.ui_config?.display_condition;
            disCondField = hf;
          }

          if (disCond) {
            const temp = this.headerFields?.find(o => disCond?.includes(o?.name));

            if (!this.form.controls[temp?.name]?.value) {
              this.form.removeControl(disCondField?.name);
            } else {
              this.form?.addControl(
                disCondField?.name,
                new FormControl({ value: null, disabled: !!disCondField?.display_only },
                  disCondField?.is_optional === false ? [Validators.required] : [],
                ),
              );
            }

            let isProgrammaticUpdate = false;

            this.form.controls[temp?.name]?.valueChanges
              .pipe(takeUntil(this.onDestroy))
              .subscribe(d => {
                const plv = temp?.ui_config?.pick_list_values?.map(o => o.label);
                if (isProgrammaticUpdate) {
                  return;
                }

                if (d === true) {
                  this.form?.addControl(
                    disCondField?.name,
                    new FormControl({ value: null, disabled: !!disCondField?.display_only },
                      disCondField?.is_optional === false ? [Validators.required] : [],
                    ),
                  );
                } else {
                  this.form.removeControl(disCondField?.name);
                }

                if (disCond.includes(d)) {
                  this.form?.addControl(
                    disCondField?.name,
                    new FormControl({ value: null, disabled: !!disCondField?.display_only },
                      disCondField?.is_optional === false ? [Validators.required] : [],
                    ),
                  );
                } else {
                  this.form.removeControl(disCondField?.name);
                }

                if (d === '_others') {
                  this.form?.addControl(
                    temp?.name + '_others',
                    new FormControl({ value: null, disabled: false },
                      [Validators.required],
                    ),
                  );
                } else {
                  this.form.removeControl(temp?.name + '_others');
                }

                const othersField = this.data?.field_values?.find(o => o.id === temp.id);

                if (temp.name === 'type_of_visual' && othersField?.value && !plv.includes(othersField?.value)) {
                  isProgrammaticUpdate = true;
                  this.form?.controls[temp.name]?.setValue('_others');
                  this.form?.controls[temp.name + '_others']?.setValue(othersField?.value);
                  isProgrammaticUpdate = false;
                }
              });
          }
        });

        const patchObject: any = {};
        const fieldValues = this.data?.field_values || [];

        (this.headerFields ?? []).forEach(field => {
          const matchedValue = fieldValues?.find((fv: any) => fv?.id === field?.id);
          if (!matchedValue) {
            return;
          }

          const val = matchedValue.value;

          let finalValue: any;
          if (Array.isArray(val)) {
            finalValue = val;
          } else if (val && typeof val === 'object') {
            finalValue = val?.value ?? val?.id ?? null;
          } else {
            finalValue = val ?? null;
          }

          patchObject[field.name] = finalValue;
        });

        this.form.patchValue(patchObject);

        if (formTypeDetails?.ui_config && formTypeDetails?.ui_config?.sections?.length > 0 && !this.dewcForm) {
          this.displayFrequency = true;
          this.frequencyOptions = formTypeDetails?.ui_config?.sections;
        }
      }

      this.createGeneralQuestionsFrom(this.generalQuestions, this.lines);

      if (this.data?.segregateLinesBySection?.general?.length > 0) {
        this.generalQuestionPositiveArray = [];
        this.generalQuestionNegativeArray = [];
        this.generalQuestionNaArray = [];

        this.data?.segregateLinesBySection?.general.forEach((line) => {
          if (line?.field_values[0]?.value === 'Yes' || line?.field_values[0]?.value === 'Pass') {
            this.generalQuestionPositiveArray.push(line);
          } else if (line?.field_values[0]?.value === 'No' || line?.field_values[0]?.value === 'Fail') {
            this.generalQuestionNegativeArray.push(line);
          } else {
            this.generalQuestionNaArray.push(line);
          }
        });
      }

      Object.keys(this.data?.segregateLinesBySection || {}).filter(o => o !== 'general').forEach((sectionName) => {
        const sectionData = this.data?.segregateLinesBySection[sectionName];
        const pass = sectionData.filter(o => o.field_values[0].value === 'Pass');
        const fail = sectionData.filter(o => o.field_values[0].value === 'Fail');
        const na = sectionData.filter(o => o.field_values[0].value === 'N/A');
        this.frequencyQuestionSummary[sectionName] = {
          answered: sectionData,
          pass,
          fail,
          na,
        };
      });

      this.form.patchValue({
        formType: this.data?.control_form?.id,
        latitude: this.data?.latitude,
        longitude: this.data?.longitude,
        location: this.data?.location,
        comment: this.data?.comment,
        pictures: this.data?.pictures ? JSON.stringify(this.data?.pictures) : JSON.stringify([]),
        videos: this.data?.videos ? JSON.stringify(this.data?.videos) : JSON.stringify([]),
        stationRangeFromStation: this.data?.station_range?.from?.station,
        stationRangeFromOffsetType: this.data?.station_range?.from?.offset_type,
        stationRangeFromOffsetDistance: this.data?.station_range?.from?.offset_distance,
        stationRangeToStation: this.data?.station_range?.to?.station,
        stationRangeToOffsetType: this.data?.station_range?.to?.offset_type,
        stationRangeToOffsetDistance: this.data?.station_range?.to?.offset_distance,
        alignment: this.data?.station_range?.alignment,
        signatureOne: this.data?.signatures[0]?.signature_text || null,
        signatureTwo: this.data?.signatures[1]?.signature_text || null,
        map_feature_link: this.data?.map_feature_link ?? null,
      }, { emitEvent: false });

      if (Object.keys(this.data?.segregateLinesBySection || {})?.length > 0) {
        const frequencyValue = Object.keys(this.data?.segregateLinesBySection).filter(o => o !== 'general');

        this.form.controls['frequency'].setValue(frequencyValue, { emitEvent: false });
        this.displayFrequencySection(frequencyValue);
      }
    }

    // Read-only display mode: disable every control (cascades to nested line /
    // section groups) so the form renders purely as a viewer, and skip the
    // editing/mutation subscriptions below.
    if (this.readOnly) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const form: any = this.getForm();
        const items = Array.isArray(data) ? data : [data];

        let mediaArray: any = [];
        let targetProperty: 'mediaFiles' | 'selectedMediaFiles' = 'mediaFiles';

        // --- 1. Determine Target Array Reference ---
        if (this.lineFields?.length && !this.dewcForm) {
          if (this.selectedLineId) {
            mediaArray = this.selectedMediaFiles;
            targetProperty = 'selectedMediaFiles'; // Set the target property name
          } else {
            mediaArray = this.mediaFiles;
          }
        } else if (this.lineFields?.length && this.dewcForm) {
          if (this.selectedSectionLineBlockId) {
            mediaArray = this.selectedMediaFiles;
            targetProperty = 'selectedMediaFiles'; // Set the target property name
          } else {
            mediaArray = this.mediaFiles;
          }
        } else {
          mediaArray = this.mediaFiles;
        }

        items.forEach((eachItem: any) => {
          const fileType = eachItem?.fileType;
          if (!fileType) {
            return;
          }

          if (fileType === 'picture') {
            const pictures = this.getPictures(form);
            pictures.push(eachItem);
            form.get('pictures')?.setValue(JSON.stringify(pictures));
          } else if (fileType === 'video') {
            const videos = this.getVideos(form);
            videos.push(eachItem);
            form.get('videos')?.setValue(JSON.stringify(videos));
          }

          mediaArray.push(eachItem);
        });

        if (targetProperty === 'selectedMediaFiles') {
          this.selectedMediaFiles = [...this.selectedMediaFiles];
        } else {
          this.mediaFiles = [...this.mediaFiles];
        }
      });

    this.dailyReportService.selectedLocation
      .pipe(takeUntil(this.onDestroy))
      .subscribe((location) => {
        if ('latitude' in (location || {}) || 'longitude' in (location || {})) {
          this.form.patchValue({
            latitude: location?.latitude,
            longitude: location?.longitude,
          });
        }

        if ('location' in (location || {}) || 'locationText' in (location || {})) {
          this.form.patchValue({
            location: location?.location ?? location?.locationText ?? null,
          });
        }

        // Only when the picker sent it: a Google / station-range pick leaves the link untouched.
        if ('map_feature_link' in (location || {})) {
          this.form.patchValue({ map_feature_link: location.map_feature_link });
          this.form.get('map_feature_link')?.markAsDirty();
        }

        // A feature linked just now in the header picker: prefill from it (ui_config.feature_source). Unlinking clears nothing.
        if (location?.map_feature_link && location?.feature_properties) {
          this.applyFeatureSourcePrefill(location.map_feature_link, location.feature_properties);
        }

        if (location?.station_range) {
          this.setStationRange(location.station_range);
        }
      });

    this.dailyReportService.addComment
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.displayCommentSection = true;
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dailyReportService.formatForms(
          this.form, this.headerFields, this.lineFields, this.dewcForm, this.data, this.signatures,
        );
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dailyReportService.delete.next({
          type: 'forms',
          id: this.data?.id,
        });
      });

    this.dailyReportService.upDateGeneralFormSummary
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.generalQuestionPositiveArray = [];
        this.generalQuestionNegativeArray = [];
        this.generalQuestionNaArray = [];
        this.answeredGeneralQuestion = [];

        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            if (data[key].field_values !== null) {
              this.answeredGeneralQuestion.push(data[key]);
            }

            if (data[key].field_values === 'Pass' || data[key].field_values === 'Yes') {
              this.generalQuestionPositiveArray.push(data[key]);
            }
            if (data[key].field_values === 'Fail' || data[key].field_values === 'No') {
              this.generalQuestionNegativeArray.push(data[key]);
            }
            if (data[key].field_values === 'N/A') {
              this.generalQuestionNaArray.push(data[key]);
            }
          }
        }
      });

    this.dailyReportService.updateFrequencyQuestionValue
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.frequencyQuestionSummary[data.key] = data?.value;
      });

    this.dailyReportService.setSignature
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        // The signature dialog emits the rich shape
        // { value, role, index, signature_method, signature_font, signed_at, pictures }.
        // `index` targets an existing signer slot in the dynamic signatures[]
        // array; a null/out-of-range index appends a new signer (roles may
        // repeat). Every captured field is merged so formatForms can thread
        // method/font/image into the saved payload — `value` maps to
        // signature_text.
        const idx = data?.index;
        const isExisting = typeof idx === 'number' && idx >= 0 && idx < this.signatures.length;
        if (isExisting) {
          this.signatures[idx] = this.applySignatureEmission(this.signatures[idx], data);
        } else {
          this.signatures.push(this.applySignatureEmission(null, data));
        }

        this.refreshSignatureViews();
        this.cdRef.detectChanges();
      });

    this.dailyReportService.setStationRange
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.setStationRange({
          ...data?.stationRange,
          alignment: data?.alignment,
        });

        this.cdRef.detectChanges();
      });
  }

  ngAfterViewInit(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  openQuestionDetails(fields: any[], type, section?) {
    if (this.isPreview) {
      this.previewQuestions.emit({
        label: section?.label || section?.name || 'Questions',
        questions: fields || [],
        options: this.responseOptions?.length ? this.responseOptions : QUESTION_DEFAULT_OPTIONS,
      });
      return;
    }

    if (this.dialogsSuppressed) {
      return;
    }
    if (section) {
      this.displayFrequencyQuestionsDetails = true;
    }
    this.displayGeneralQuestionsDetails = true;
    this.dialog.open(QuestionsComponent, {
      disableClose: true,
      width: '1200px',
      height: '620px',
      panelClass: 'custom-dialog-container',
      data: {
        fields: JSON.parse(JSON.stringify(fields)),
        form: this.form,
        type,
        frequency: section,
        responseOptions: this.responseOptions || [],
      },
    });
  }

  displayFrequencySection(data = []) {
    this.selectedFrequencyType = [];
    const tempData = [];
    data.forEach((d) => {
      tempData.push(this.frequencyOptions.filter(o => o.name === d)[0]);
    });
    this.selectedFrequencyType = tempData;
    this.displayFrequencyQuestions = true;
  }

  /**
   * Role options for the signature dialog, sourced from the form's
   * ui_config.signature_roles (array of label strings), falling back to the
   * legacy QA/QC pair when the template doesn't configure roles. Roles may
   * repeat across signers, so this list is NOT filtered by already-used roles.
   * label === value (the role label is the canonical identifier).
   */
  getSignatureRoleOptions(): { label: string; value: string }[] {
    return buildSignatureRoleOptions(this.formTypeDetails?.ui_config);
  }

  /**
   * Open the signature capture dialog. `index` targets an existing signer slot
   * for editing; pass null/omit to append a brand-new signer. Roles come from
   * ui_config.signature_roles (QA/QC fallback) and may repeat.
   */
  openSignatureDetails(index: number | null = null) {
    if (this.isPreview) {
      this.previewSignature.emit();
      return;
    }
    if (this.dialogsSuppressed) {
      return;
    }

    this.dialog.open(SignatureComponent, {
      disableClose: true,
      width: '580px',
      maxHeight: '90vh',
      data: {
        index,
        signer: index != null ? this.signatures?.[index] : null,
        form: this.form,
        options: this.getSignatureRoleOptions(),
        // Capture mode: when true the dialog uses the draw pad; otherwise it
        // captures a typed signature name. Defaults to false (typed).
        signaturePadEnabled: !!this.formTypeDetails?.ui_config?.signature_pad?.enabled,
        // Per-form gate (draw-pad mode only): when true the printed name is
        // mandatory. Defaults to false when the template doesn't configure it.
        requirePrintedName: !!this.formTypeDetails?.ui_config?.signature_pad?.require_printed_name,
      },
    });
  }

  /** Remove a captured signer by index, then rebuild the display models. */
  removeSignature(index: number) {
    if (this.readOnly) {
      return;
    }
    if (index < 0 || index >= (this.signatures?.length ?? 0)) {
      return;
    }
    this.signatures.splice(index, 1);
    this.refreshSignatureViews();
    this.cdRef.detectChanges();
  }

  openStationRangeDetails() {
    if (this.dialogsSuppressed) {
      return;
    }
    this.dialog.open(StationRangeComponent, {
      disableClose: true,
      width: '1200px',
      height: '620px',
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: 'station-range-dialog-panel',
      data: {
        form: this.form,
      },
    });
  }

  /** W49: whether the project has published sheets, for "Mark on drawing" in the location menu (once per project) */
  private checkPublishedSheets(projectId: string | null | undefined): void {
    if (!projectId || projectId === this.sheetsCheckedFor) {
      return;
    }
    this.sheetsCheckedFor = projectId;
    this.hasPublishedSheets = false;
    this.drawingsAvailability.available(projectId).then(found => {
      if (this.sheetsCheckedFor === projectId) {
        this.hasPublishedSheets = found;
        this.cdRef.markForCheck();
      }
    }).catch(() => undefined);
  }

  /** W49: "Mark on drawing": the location picker, on its Drawing tab */
  markOnDrawing() {
    this.drawingsTrack.track('mark_on_drawing_clicked', { record_type: 'form' });
    this.openLocationPicker('drawing');
  }

  openLocationPicker(initialTab?: 'drawing') {
    if (this.dialogsSuppressed) {
      return;
    }
    const formLat = this.form?.controls?.['latitude']?.value;
    const formLng = this.form?.controls?.['longitude']?.value;
    const locationText = this.form?.controls?.['location']?.value || '';

    if (formLat && formLng) {
      this.openPinPicker(formLat, formLng, locationText, initialTab);
      return;
    }

    const loc = this.defaultPickerSeed();
    this.openPinPicker(loc?.latitude, loc?.longitude, locationText, initialTab);
  }

  /** Where a picker opens without a record pin: the device location, else the site, else the project. */
  private defaultPickerSeed(): PickerSeed | null {
    let loc: PickerSeed | null = null;
    this.store.select(currentLocation)
      .subscribe(location => {
        loc = location;
        if (!loc?.latitude || !loc?.longitude) {
          this.store.select(siteDetails)
            .subscribe(site => {
              if (site?.longitude && site?.latitude) {
                loc = { latitude: site?.latitude, longitude: site?.longitude };
              } else {
                this.store.select(projectDetails)
                  .subscribe(project => {
                    if (project?.longitude && project?.latitude) {
                      loc = { latitude: project?.latitude, longitude: project?.longitude };
                    }
                  }).unsubscribe();
              }
            }).unsubscribe();
        }
      }).unsubscribe();
    return loc;
  }

  /** Selector options: hides `launcher_only` forms when adding, keeping the one selected (a routed target). */
  private refreshFormTypeOptions() {
    const current = this.form?.controls?.['formType']?.value;
    const next = formTypeChoices(this.fromTypeOptions, !this.data, current);
    if (next.length !== this.formTypeOptions.length || next.some((form, index) => form !== this.formTypeOptions[index])) {
      this.formTypeOptions = next;
    }
  }

  /**
   * Map-first launcher (ui_config.feature_launcher): open the ArcGIS picker on the launcher layer; linking a feature
   * opens the routed target form (by control_form `type`) linked and prefilled. Cancel goes back to choosing a form.
   */
  private async startFeatureLauncher(launcherForm: LauncherFormLike) {
    const launcher = featureLauncherOf(launcherForm);
    const routes = resolveLauncherRoutes(launcher, this.fromTypeOptions);
    const previous = {
      latitude: this.form.controls['latitude']?.value,
      longitude: this.form.controls['longitude']?.value,
      map_feature_link: this.form.get('map_feature_link')?.value ?? null,
    };
    this.launcherMessage = '';
    if (!routes.length) {
      this.cancelFeatureLauncher(previous, 'No form is set up for this map pick.');
      return;
    }
    if (this.dialogsSuppressed) {
      this.cancelFeatureLauncher(previous);
      return;
    }
    const seed = this.defaultPickerSeed();
    let ref: Awaited<ReturnType<LocationPickerLauncherService['open']>>;
    try {
      ref = await this.locationPickerLauncher.open({
        latitude: seed?.latitude,
        longitude: seed?.longitude,
        enableLocationSection: false,
        existingLink: null,
        recordLabel: 'form',
        designContextOnly: true,
        featureLauncher: {
          layer: launcher.layer, title: launcher.title, label_fields: launcher.label_fields, routes,
          multi_value_fields: launcher.multi_value_fields,
          currentReport: { id: this.dailyReport?.id, uuid: this.dailyReport?.uuid },
        },
      });
    } catch (error) {
      console.error('Location picker failed to open', error);
      ref = null;
    }
    if (!ref) {
      this.cancelFeatureLauncher(previous, 'The map for this form isn\'t available right now.');
      return;
    }
    ref.afterClosed().pipe(take(1)).subscribe(result => this.finishFeatureLauncher(result as PickResult, routes, previous));
  }

  private finishFeatureLauncher(result: PickResult | undefined, routes: ResolvedLauncherRoute[], previous: Record<string, unknown>) {
    if (result?.open_entry) {
      this.openLinkedEntry(result.open_entry, previous);
      return;
    }
    const link = result?.map_feature_link;
    const properties = result?.feature_properties;
    const route = link && properties ? matchLauncherRoute(routes, properties) : null;
    if (!route) {
      this.cancelFeatureLauncher(previous);
      return;
    }
    const formId = route.form.id;
    this.form.controls['formType'].setValue(formId);
    // The selector may have opened an existing single-entry form instead (allow_multiple_forms off).
    if (this.formTypeDetails?.id !== formId) {
      return;
    }
    this.form.patchValue({
      latitude: link.pin?.latitude ?? result.latitude,
      longitude: link.pin?.longitude ?? result.longitude,
      map_feature_link: link,
    });
    // The TARGET form's feature_source (now formTypeDetails) maps the feature onto its header fields.
    this.applyFeatureSourcePrefill(link, properties);
    this.cdRef.detectChanges();
  }

  /**
   * "Open existing entry" on a done launcher row: the entry is in this report (the picker only offers it then), so
   * open it in the editor; if this report no longer lists it, say where it was saved.
   */
  private openLinkedEntry(entry: FeatureLinkEntry, previous: Record<string, unknown>) {
    const formEntry = findLinkedRecord(this.controlForms, entry);
    if (!formEntry) {
      const date = shortReportDate(entry.reportDate);
      this.cancelFeatureLauncher(previous, date ? `Saved on ${date} report.` : 'Saved on another report.');
      return;
    }
    this.cancelFeatureLauncher(previous);
    this.dailyReportService.openDetailsPanel.next({ type: 'forms', formData: formEntry });
  }

  /** Back to choosing a form, nothing created; restores what the picker may have patched. */
  private cancelFeatureLauncher(previous: Record<string, unknown>, message = '') {
    this.form.patchValue(previous, { emitEvent: false });
    this.form.controls['formType'].setValue('');
    this.launcherMessage = message;
    this.cdRef.detectChanges();
  }

  private openPinPicker(latitude: number | string | null, longitude: number | string | null, locationText: string, initialTab?: 'drawing') {
    this.locationPickerLauncher.open({
      latitude,
      longitude,
      enableLocationSection: true,
      locationText,
      // Null when the form has no link control: the pick then leaves the record's link untouched unless the user links.
      existingLink: this.form?.get('map_feature_link')?.value ?? null,
      recordLabel: 'form',
      ...(initialTab ? { initialTab } : {}),
      featureSource: this.featureSource,
    }).catch(error => console.error('Location picker failed to open', error));
  }

  /** The "<n> fields autofilled" card's text (null hides it: nothing autofilled is left). */
  get autofillCard(): { title: string; subtitle: string } | null {
    const count = this.prefillCaptions.count();
    return count ? autofillSummary(count, this.prefillCaptions.source()) : null;
  }

  /** For DailyReportService.formPickerContext: read at pick time, so it follows the form type and link. */
  private readonly pickerContext = (): FormPickerContext => ({
    featureSource: this.featureSource,
    existingLink: this.form?.get('map_feature_link')?.value ?? null,
    latitude: this.form?.controls?.['latitude']?.value ?? null,
    longitude: this.form?.controls?.['longitude']?.value ?? null,
  });

  /** `ui_config.feature_source` of the form being edited, if it has one. */
  get featureSource(): FeatureSource | null {
    const source = this.formTypeDetails?.ui_config?.feature_source;
    return source && typeof source === 'object' && source.layer ? source : null;
  }

  /**
   * Overwrites the header fields the linked feature resolves (see resolveFeatureSourcePrefill); others are left alone.
   * `_bid_item` resolves against the same list its dropdown offers (bidItemsList: value = bid item id, matched on `item`).
   */
  private applyFeatureSourcePrefill(link: MapFeatureLink, properties: Record<string, unknown>) {
    const featureSource = this.featureSource;
    if (this.readOnly || !this.form || !featureSourceMatchesLink(featureSource, link)) {
      return;
    }
    const values = resolveFeatureSourcePrefill(featureSource, properties, this.headerFields, { bidItems: this.bidItemsList });
    // get(), not contains(): contains() is false for disabled controls.
    const patch = Object.fromEntries(Object.entries(values).filter(([name]) => !!this.form.get(name)));
    if (!Object.keys(patch).length) {
      return;
    }
    this.form.patchValue(patch);
    this.form.markAsDirty();
    // After the patch, so its own value changes do not count as edits.
    const controls = Object.fromEntries(Object.keys(patch).map(name => [name, this.form.get(name)]));
    this.prefillCaptions.track(controls, link?.layer_name || '');
  }

  deletePicture(pic) {
    const form: any = this.getForm();

    if (pic.fileType === 'picture') {
      let pictures = this.getPictures(form);

      pictures = pictures?.filter(o => o.id !== pic.id);

      if (this.lineFields?.length && !this.dewcForm) {
        if (this.selectedLineId) {
          this.selectedMediaFiles = this.selectedMediaFiles?.filter(o => o.id !== pic.id);
        } else {
          this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
        }
      } else if (this.lineFields?.length && this.dewcForm) {
        if (this.selectedSectionLineBlockId) {
          this.selectedMediaFiles = this.selectedMediaFiles?.filter(o => o.id !== pic.id);
        } else {
          this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
        }
      } else {
        this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      }

      form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos(form);

      videos = videos?.filter(o => o.id !== pic.id);

      if (this.lineFields?.length && !this.dewcForm) {
        if (this.selectedLineId) {
          this.selectedMediaFiles = this.selectedMediaFiles?.filter(o => o.id !== pic.id);
        } else {
          this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
        }
      } else if (this.lineFields?.length && this.dewcForm) {
        if (this.selectedSectionLineBlockId) {
          this.selectedMediaFiles = this.selectedMediaFiles?.filter(o => o.id !== pic.id);
        } else {
          this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
        }
      } else {
        this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      }

      form?.get('videos')?.setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  /** W77 (review I1): the header "…" Clear drops the link too, so markup on it asks first (link-removal.ts) */
  deleteLocation() {
    const link = this.form?.get('map_feature_link')?.value ?? null;
    confirmLinkRemoval(this.dialog, link, linkHeldStrokes(this.injector, link)).subscribe(go => {
      if (go) this.clearLocationState();
    });
  }

  private clearLocationState() {
    this.form?.get('latitude').setValue(null);
    this.form?.get('longitude').setValue(null);
    this.form?.get('location')?.setValue(null);
    // Deleting the location removes the link with it: an explicit removal (R54)
    if (this.form?.get('map_feature_link')?.value) this.form.get('map_feature_link').markAsDirty();
    this.form?.get('map_feature_link')?.setValue(null);
    this.form?.patchValue({
      stationRangeFromStation: null,
      stationRangeFromOffsetType: null,
      stationRangeFromOffsetDistance: null,
      stationRangeToStation: null,
      stationRangeToOffsetType: null,
      stationRangeToOffsetDistance: null,
      alignment: null,
    });
    this.stationRange = {};
    this.stationRangeDisplayValue = null;
    this.alignment = null;
  }

  showLocationSection(): boolean {
    const hasGps = this.form?.controls?.['latitude']?.value !== null &&
      this.form?.controls?.['longitude']?.value !== null &&
      this.form?.controls?.['latitude']?.value !== '' &&
      this.form?.controls?.['longitude']?.value !== '';
    // W50: a sheet-only drawing link (no pin) is the location too; the shared card shows its strip
    return hasRecordLocation({ hasGps, link: this.form?.controls?.['map_feature_link']?.value?.sheet ? this.form.controls['map_feature_link'].value : null });
  }

  showLocationOrStationSection() {
    return this.showLocationSection() || this.checkStationFormData();
  }

  isStationRangeLocationEnabledForForms() {
    const mode = [
      this.project?.daily_report_location_mode,
      this.project?.owner_organization?.daily_report_location_mode,
      this.dailyReportLocationConfig?.mode,
    ].find(value => ['pin', 'station_range'].includes(value));

    if (mode !== 'station_range') {
      return false;
    }

    const widgets = this.dailyReportLocationConfig?.widgets || [];
    return !widgets.length || widgets.includes('control_forms');
  }

  private shouldDisplayStationRange(formTypeDetails: any) {
    return !!formTypeDetails?.ui_config?.display_station_range ||
      !!formTypeDetails?.ui_config?.allow_station_range ||
      this.isStationRangeLocationEnabledForForms();
  }

  private buildStationRangeDisplayValue() {
    if (!this.checkStationFormData()) {
      return null;
    }

    return {
      ...(this.stationRange || {}),
      from: {
        station: this.stationRange?.from?.station ?? this.form?.controls?.['stationRangeFromStation']?.value,
        offsetType: this.stationRange?.from?.offsetType ?? this.form?.controls?.['stationRangeFromOffsetType']?.value,
        offsetDistance: this.stationRange?.from?.offsetDistance ?? this.form?.controls?.['stationRangeFromOffsetDistance']?.value,
      },
      to: {
        station: this.stationRange?.to?.station ?? this.form?.controls?.['stationRangeToStation']?.value,
        offsetType: this.stationRange?.to?.offsetType ?? this.form?.controls?.['stationRangeToOffsetType']?.value,
        offsetDistance: this.stationRange?.to?.offsetDistance ?? this.form?.controls?.['stationRangeToOffsetDistance']?.value,
      },
      alignment: this.alignment ?? this.stationRange?.alignment ?? this.form?.controls?.['alignment']?.value,
    };
  }

  openLocationOrStationDetails() {
    if (this.checkStationFormData() || this.displayStationRange) {
      this.openStationRangeDetails();
    }
  }

  private setStationRange(stationRange: any = {}) {
    this.stationRange = {
      from: {
        station: stationRange?.from?.station,
        offsetType: stationRange?.from?.offsetType ?? stationRange?.from?.offset_type,
        offsetDistance: stationRange?.from?.offsetDistance ?? stationRange?.from?.offset_distance,
      },
      to: {
        station: stationRange?.to?.station,
        offsetType: stationRange?.to?.offsetType ?? stationRange?.to?.offset_type,
        offsetDistance: stationRange?.to?.offsetDistance ?? stationRange?.to?.offset_distance,
      },
      alignment: stationRange?.alignment,
    };
    this.alignment = stationRange?.alignment;
    this.form?.patchValue({
      stationRangeFromStation: this.stationRange?.from?.station,
      stationRangeFromOffsetType: this.stationRange?.from?.offsetType,
      stationRangeFromOffsetDistance: this.stationRange?.from?.offsetDistance,
      stationRangeToStation: this.stationRange?.to?.station,
      stationRangeToOffsetType: this.stationRange?.to?.offsetType,
      stationRangeToOffsetDistance: this.stationRange?.to?.offsetDistance,
      alignment: this.alignment,
    });
    this.stationRangeDisplayValue = this.buildStationRangeDisplayValue();
  }

  getPictures(form): any[] {
    try {
      const pictures = form.controls['pictures']?.value;
      return JSON.parse(pictures) || [];
    } catch (e) {
      return [];
    }
  }

  getVideos(form): any[] {
    try {
      const videos = form.controls['videos']?.value;
      return JSON.parse(videos) || [];
    } catch (e) {
      return [];
    }
  }

  createGeneralQuestionsFrom(fields: any, lines: any) {
    this.form?.removeControl('questions', { emitEvent: false });
    const parentFormBuilder = {};

    fields.forEach((field: any) => {
      const matchingLine = lines.find((line) => line?.field_values[0]?.id === field?.id);

      const questionId = matchingLine
        ? `${matchingLine?.id}_${field?.id}`
        : `new_${new Date().getTime()}_${field?.id}`;

      parentFormBuilder[questionId] = this.fb.group({
        field_values: new FormControl(lines.find(o => o.field_values[0]?.id === field.id)?.field_values?.[0].value || null),
        comment: new FormControl(lines.find(o => o.field_values[0]?.id === field.id)?.comment),
        pictures: new FormControl(JSON.stringify(lines.find(o => o.field_values[0]?.id === field.id)?.pictures || [])),
        videos: new FormControl(JSON.stringify(lines.find(o => o.field_values[0]?.id === field.id)?.videos || [])),
      }, { emitEvent: false });
    });

    this.form?.addControl('questions', this.fb.group(parentFormBuilder, { emitEvent: false }));
  }

  createFrequencyQuestionsFrom(fields: any, lines: any, key: any) {
    this.form?.removeControl(`${key}_questions`, { emitEvent: false });
    const parentFormBuilder = {};

    fields.forEach((field: any) => {
      const matchingLine = lines.find((line) => line?.field_values[0]?.id === field?.id);

      const questionId = matchingLine
        ? `${matchingLine.id}_${field.id}`
        : `new_${new Date().getTime()}_${field?.id}`;

      parentFormBuilder[questionId] = this.fb.group({
        field_values: new FormControl(lines.find(o => o.field_values[0]?.id === field.id)?.field_values?.[0].value || null),
        comment: new FormControl(lines.find(o => o.field_values[0]?.id === field.id)?.comment),
        pictures: new FormControl(JSON.stringify(lines.find(o => o.field_values[0]?.id === field.id)?.pictures || [])),
        videos: new FormControl(JSON.stringify(lines.find(o => o.field_values[0]?.id === field.id)?.videos || [])),
      }, { emitEvent: false });
    });
    this.form?.addControl(`${key}_questions`, this.fb.group(parentFormBuilder), { emitEvent: false });
  }

  checkStationFormData() {
    const stationRangeFromStationValue = this.form.controls?.['stationRangeFromStation'].value;
    const stationRangeFromOffsetTypeValue = this.form.controls?.['stationRangeFromOffsetType'].value;
    const stationRangeFromOffsetDistanceValue = this.form.controls?.['stationRangeFromOffsetDistance'].value;
    const stationRangeToStationValue = this.form.controls?.['stationRangeToStation'].value;
    const stationRangeToOffsetTypeValue = this.form.controls?.['stationRangeToOffsetType'].value;
    const stationRangeToOffsetDistanceValue = this.form.controls?.['stationRangeToOffsetDistance'].value;
    const alignment = this.form.controls?.['alignment'].value;

    return !!(
      stationRangeFromStationValue
      || stationRangeFromOffsetTypeValue
      || stationRangeFromOffsetDistanceValue
      || stationRangeToStationValue
      || stationRangeToOffsetTypeValue
      || stationRangeToOffsetDistanceValue
      || alignment
    );
  }

  isValidObject(obj: any): boolean {
    return obj && Object.values(obj).some(value => value !== null && value !== undefined && value !== '');
  }

  /**
   * Rebuild the read-only signature presentation models from `this.signatures`.
   * Only signatures that carry text or a drawn image are shown.
   */
  refreshSignatureViews(): void {
    // Map THEN filter so each view keeps its source index into signatures[]
    // (the list is sparse-filtered for display but edit/remove target the real
    // array position). A signer is shown if it has a printed name, a resolved
    // image, or any captured picture attachment.
    this.signatureViews = (this.signatures || [])
      .map((s: any, i: number) => ({ view: buildSignatureView(s, undefined, i), raw: s }))
      // A signer is shown if it has a printed name or a resolved image. buildSignatureView
      // already resolves the drawn image across every attachment URL field the API uses
      // (original_url/preview_url/thumb_url/...), so `view.imageUrl` is the single source
      // of truth — drawn signatures have an empty signature_text and would otherwise be
      // filtered out.
      .filter(({ view }: { view: SignatureView }) => view.text || view.imageUrl)
      .map(({ view }: { view: SignatureView }) => view);
  }

  /**
   * Instance wrapper around mergeSignatureEmission so the subscriber stays terse.
   */
  applySignatureEmission(existing: any, emission: any): any {
    return mergeSignatureEmission(existing, emission);
  }

  createPicklistOptions() {
    if (!this.project) {
      return;
    }

    const allFields = [
      ...(this.headerFields ?? []),
      ...(this.footerFields ?? []),
    ];

    const options: Record<string, any[]> = { ...this.picklistOptions };

    allFields.forEach(o => {
      const [key, labelToUse] = o?.pick_list?.split?.('.') ?? [];

      if (!key && o.name !== '_bid_item') {
        return;
      }

      if (key === 'activity_codes') {
        options[o.name] = this.project?.activity_codes_forms ?? [];
      } else {
        const sourceArray = this.project?.[key] ?? [];
        options[o.name] = sourceArray.map(item => ({
          ...item,
          label: item?.[labelToUse] ?? '',
          // Store the pick_list-designated property (e.g. the contractor name from
          // "sub_contractors.name") as the saved value — not the record id — so
          // field_values match the mobile standard and render correctly on mobile.
          value: item?.[labelToUse] ?? '',
        }));
      }

      if (o.name === '_bid_item') {
        options[o.name] = [...(this.bidItemsList ?? [])];
      }
    });

    this.picklistOptions = options;
  }

  onToggle($event: any, controlName: any) {
    this.form.controls[controlName]?.setValue($event?.checked);
  }

  onToggleLf($event: any, controlName: any) {
    const value = $event.checked || false;
    this.lineFormGroup.controls[controlName].setValue(value);
  }

  onToggleSectionField($event: any, controlName: any) {
    const value = $event.checked || false;
    this.selectedSectionLineBlockFormGroup?.controls[controlName].setValue(value);
  }

  ngOnDestroy() {
    if (this.dailyReportService.formPickerContext === this.pickerContext) {
      this.dailyReportService.formPickerContext = null;
    }
    this.prefillCaptions.clear();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  addEntry() {
    if (this.readOnly) {
      return;
    }
    let index = 1;
    let lineKey = `line_${index}`;
    while (this.form.contains(lineKey)) {
      index++;
      lineKey = `line_${index}`;
    }
    const newLineGroup = new FormGroup({});

    this.lineFields.forEach((field: any) => {
      const isDisabled = !!field?.display_only;
      const isRequired = field?.is_optional === false;
      const validators = isRequired ? [Validators.required] : [];

      if (field.type === 'time') {
        newLineGroup?.addControl(
          field?.name,
          new FormControl({ value: null, disabled: isDisabled }, validators),
        );

        newLineGroup?.addControl(
          `${field.name}_timePeriod`,
          new FormControl({ value: null, disabled: false }),
        );

        newLineGroup?.addControl(
          `${field.name}_am_pm`,
          new FormControl({ value: null, disabled: false }),
        );
      } else {
        newLineGroup?.addControl(
          field?.name,
          new FormControl({ value: null, disabled: isDisabled }, validators),
        );
      }
    });

    newLineGroup.addControl(
      'pictures',
      new FormControl({ value: JSON.stringify([]), disabled: false }),
    );

    newLineGroup.addControl(
      'videos',
      new FormControl({ value: JSON.stringify([]), disabled: false }),
    );

    this.form.addControl(lineKey, newLineGroup);

    this.lineKeys = this.getLineKeys() || [];
    this.selectLine(lineKey);
  }

  selectLine(key: any) {
    this.selectedLineId = this.selectedLineId === key ? null : key;

    this.lineFormGroup = this.form.get(key) as FormGroup;

    const tempPic = this.getPictures(this.lineFormGroup) || [];
    const tempVideo = this.getVideos(this.lineFormGroup) || [];

    this.selectedMediaFiles = [
      ...tempPic,
      ...tempVideo,
    ];

    this.lineFields.forEach((field: any) => {
      if (field?.dependent_fields?.length) {
        this.lineFormGroup.controls[field.name].valueChanges
          .pipe(debounceTime(200), takeUntil(this.onDestroy))
          .subscribe(() => {
            const dependentFields = field.dependent_fields;
            dependentFields.forEach(o => {
              const rawFormula = o?.expression;
              const formula = rawFormula?.replace(/\s+/g, ' ').trim();
              if (!formula || typeof formula !== 'string') {
                return;
              }

              try {
                const variableNames = [];
                const formulaWords = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
                let formControlNames = [];
                const lineFormGroupControlName = Object.keys(this.lineFormGroup.controls);

                formControlNames = [...lineFormGroupControlName];

                formulaWords.forEach(word => {
                  if (formControlNames.includes(word)) {
                    variableNames.push(word);
                  }
                });

                let canEvaluate = true;
                const formValues: any = {};
                variableNames.forEach(varName => {
                  const value = this.lineFormGroup?.controls?.[varName]?.value || this.form?.controls?.[varName]?.value;

                  if (value === null || value === undefined || value === '') {
                    canEvaluate = false;
                  } else {
                    const numericValue = Number(value);
                    if (isNaN(numericValue)) {
                      canEvaluate = false;
                    } else {
                      formValues[varName] = numericValue;
                    }
                  }
                });


                if (!canEvaluate) {
                  return;
                }

                const result = this.evaluator.evaluateExpression(formula, formValues);
                const currentVal = this.lineFormGroup.controls[o.name].value;
                if (currentVal !== result) {
                  this.lineFormGroup.controls[o.name].setValue(result, { emitEvent: false });
                }
              } catch (err) {
                console.error(err);
              }

            });
          });
      }
    });

    this.evaluateLineFormulas(this.lineFormGroup);
  }

  private evaluateLineFormulas(lineFormGroup: FormGroup): void {
    if (!this.lineFields?.length || !lineFormGroup) {
      return;
    }
    this.lineFields.forEach((field: any) => {
      if (!field?.dependent_fields?.length) {
        return;
      }
      const dependentFields = field.dependent_fields;
      dependentFields.forEach((o: any) => {
        const rawFormula = o?.expression;
        const formula = rawFormula?.replace(/\s+/g, ' ').trim();
        if (!formula || typeof formula !== 'string') {
          return;
        }
        try {
          const formulaWords = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
          const formControlNames = Object.keys(lineFormGroup.controls || {});
          const variableNames = formulaWords.filter((word: string) => formControlNames.includes(word));
          let canEvaluate = true;
          const formValues: any = {};
          variableNames.forEach((varName: string) => {
            const value = lineFormGroup?.controls?.[varName]?.value ?? this.form?.controls?.[varName]?.value;
            if (value === null || value === undefined || value === '') {
              canEvaluate = false;
            } else {
              const numericValue = Number(value);
              if (isNaN(numericValue)) {
                canEvaluate = false;
              } else {
                formValues[varName] = numericValue;
              }
            }
          });
          if (!canEvaluate) {
            return;
          }
          const result = this.evaluator.evaluateExpression(formula, formValues);
          const currentVal = lineFormGroup.controls[o.name]?.value;
          if (lineFormGroup.controls[o.name] && currentVal !== result) {
            lineFormGroup.controls[o.name].setValue(result, { emitEvent: false });
          }
        } catch (err) {
          console.error(err);
        }
      });
    });
  }

  deleteLineEntry(key: any) {
    this.form.removeControl(key, { emitEvent: true });
    this.lineKeys = this.lineKeys.filter(o => o.key !== key);
    this.cdRef.detectChanges();
  }

  getLineKeys() {
    this.lineKeys = [];
    return Object.keys(this.form.controls || {})
      .filter(key => key.startsWith('line_'))
      .map(key => {
        return { key, lineName: this.lineFields?.find(o => o?.display_order === 1)?.label };
      });
  }

  selectSection(section: any) {
    if (this.selectedSectionId === section?.name) {
      this.selectedSectionId = null;
      this.selectedSectionLineBlockId = null;
    } else {
      this.selectedSectionId = section?.name;
      this.selectedSectionLineBlockId = null;
    }

    this.selectedSectionFormGroup = this.form.get(`section_${section?.name}`) as FormGroup;
    this.sectionLineFields = this.sectionTypeFieldsMap[this.selectedSectionId];
    this.selectedSectionLineKeys = this.getSelectedSectionLineKeys() || [];
  }

  createSectionTypeFieldsMap(fields: any, sections: any) {
    this.sectionTypeFieldsMap = {};
    sections.forEach(o => {
      this.sectionTypeFieldsMap[o?.name] = fields
        .filter(field => field?.ui_config?.section_name === o?.name)
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
    });
  }

  addSectionLine(sa: any) {
    if (this.readOnly) {
      return;
    }
    this.selectedSectionId = sa?.name;
    this.selectedSectionFormGroup = this.form.get(`section_${sa?.name}`) as FormGroup;

    this.sectionLineFields = this.sectionTypeFieldsMap[sa?.name];

    let index = 1;
    let sectionLineKey = `line_${index}`;

    while (this.selectedSectionFormGroup.contains(sectionLineKey)) {
      index++;
      sectionLineKey = `line_${index}`;
    }

    const newSectionLineGroup = new FormGroup({});

    this.sectionLineFields.forEach((field: any) => {
      const isDisabled = !!field?.display_only;
      const isRequired = field?.is_optional === false;
      const validators = isRequired ? [Validators.required] : [];
      const value = field?.default_value ?? null;

      newSectionLineGroup.addControl(
        field.name,
        new FormControl({ value, disabled: isDisabled }, validators),
      );
    });

    this.selectedSectionFormGroup?.addControl(sectionLineKey, newSectionLineGroup);

    this.selectedSectionLineKeys = this.getSelectedSectionLineKeys() || [];
    this.selectSectionLineBlock(sectionLineKey);
  }

  selectSectionLineBlock(slk: any) {
    this.selectedSectionLineBlockId = this.selectedSectionLineBlockId === slk ? null : slk;
    this.selectedSectionLineBlockFormGroup = this.selectedSectionFormGroup.get(slk) as FormGroup;

    const tempPic = this.getPictures(this.selectedSectionLineBlockFormGroup) || [];
    const tempVideo = this.getVideos(this.selectedSectionLineBlockFormGroup) || [];

    this.selectedMediaFiles = [
      ...tempPic,
      ...tempVideo,
    ];

    this.sectionLineFields?.forEach((field: any) => {
      if (field?.dependent_fields?.length) {
        this.selectedSectionLineBlockFormGroup?.controls[field.name]?.valueChanges
          .pipe(debounceTime(200), takeUntil(this.onDestroy))
          .subscribe(() => {
            const dependentFields = field.dependent_fields;
            dependentFields.forEach(o => {
              const rawFormula = o?.expression;
              const formula = rawFormula?.replace(/\s+/g, ' ').trim();
              if (!formula || typeof formula !== 'string') {
                return;
              }

              try {
                const variableNames = [];
                const formulaWords = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
                let formControlNames = [];
                const selectedSectionLineBlockFormGroupControlName = Object.keys(this.selectedSectionLineBlockFormGroup?.controls);

                formControlNames = [...selectedSectionLineBlockFormGroupControlName];

                formulaWords.forEach(word => {
                  if (formControlNames.includes(word)) {
                    variableNames.push(word);
                  }
                });

                let canEvaluate = true;
                const formValues: any = {};
                variableNames.forEach(varName => {
                  const value = this.selectedSectionLineBlockFormGroup?.controls?.[varName]?.value;

                  if (value === null || value === undefined || value === '') {
                    canEvaluate = false;
                  } else {
                    const numericValue = Number(value);
                    if (isNaN(numericValue)) {
                      canEvaluate = false;
                    } else {
                      formValues[varName] = numericValue;
                    }
                  }
                });


                if (!canEvaluate) {
                  return;
                }

                const result = this.evaluator.evaluateExpression(formula, formValues);
                const currentVal = this.selectedSectionLineBlockFormGroup?.controls[o.name].value;
                if (currentVal !== result) {
                  this.selectedSectionLineBlockFormGroup?.controls[o.name].setValue(result, { emitEvent: false });
                }
              } catch (err) {
                console.error(err);
              }

            });
          });
      }
    });
  }

  getSelectedSectionLineKeys(): any {
    return Object.keys(this.selectedSectionFormGroup.controls || {})
      .filter((key) => key.startsWith('line_'))
      .map((key, index) => {
        const lineLabel = `Line ${index + 1}`;
        return { key, lineLabel };
      });
  }

  deleteSectionLine(key: any) {
    this.selectedSectionLineKeys = this.selectedSectionLineKeys.filter(o => o.key !== key);
    this.selectedSectionFormGroup.removeControl(key);
  }

  getForm() {
    let result: FormGroup | null;
    if (this.lineFields?.length && !this.dewcForm) {
      if (this.selectedLineId) {
        result = this.form?.get(this.selectedLineId) as FormGroup | null;
      } else {
        result = this.form;
      }
    } else if (this.lineFields?.length && this.dewcForm) {
      if (this.selectedSectionId && !this.selectedSectionLineBlockId) {
        result = this.form;
      } else if (this.selectedSectionId && this.selectedSectionLineBlockId) {
        const sectionForm = this.form.get(`section_${this.selectedSectionId}`) as FormGroup | null;
        const sectionLineBlockForm = sectionForm?.get(this.selectedSectionLineBlockId) as FormGroup | null;
        result = sectionLineBlockForm ?? this.form;
      } else {
        result = this.form;
      }
    } else {
      result = this.form;
    }
    return result;
  }

  private evaluateSectionLineFormulas(): void {
    Object.entries(this.form.controls || {})
      .filter(([key, _]) => key?.startsWith('section_'))
      .forEach(([key, control]: [string, FormGroup]) => {
        const tempKey = key.split('_')[1];
        const sectionLineFields = this.sectionTypeFieldsMap?.[tempKey] || [];

        Object.entries(control.controls || {}).forEach(([_, cntrl]: [string, FormGroup]) => {
          sectionLineFields.forEach((field: any) => {
            if (field?.dependent_fields?.length) {
              const dependentFields = field?.dependent_fields;

              dependentFields.forEach(o => {
                const rawFormula = o?.expression;
                const formula = rawFormula?.replace(/\s+/g, ' ').trim();
                if (!formula || typeof formula !== 'string') {
                  return;
                }

                try {
                  const variableNames = [];
                  const formulaWords = formula?.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];

                  const formControlNames = [
                    ...Object.keys(cntrl.controls),
                  ];

                  formulaWords.forEach(word => {
                    if (formControlNames.includes(word)) {
                      variableNames.push(word);
                    }
                  });

                  let canEvaluate = true;
                  const formValues: any = {};

                  variableNames.forEach(varName => {
                    const value = cntrl?.controls?.[varName]?.value;

                    if (value === null || value === undefined || value === '') {
                      canEvaluate = false;
                    } else {
                      const numericValue = Number(value);
                      if (isNaN(numericValue)) {
                        canEvaluate = false;
                      } else {
                        formValues[varName] = numericValue;
                      }
                    }
                  });

                  if (!canEvaluate) {
                    return;
                  }

                  const result = this.evaluator.evaluateExpression(formula, formValues);
                  const currentVal = cntrl.controls[o.name].value;

                  if (currentVal !== result) {
                    cntrl.controls[o.name].setValue(result, { emitEvent: false });
                  }
                } catch (err) {
                  console.error(err);
                }
              });
            }
          });
        });
      });
  }
}
